import React, { useEffect, useRef, useState } from 'react';
import { Card } from 'primereact/card';

// ==================== TYPES ====================
export interface UnmarkedClass {
  class_id: string;
  class_name: string;
  section: string;
  status?: 'marked' | 'unmarked' | 'partially_marked';
  total_students: number;
  attendance_count: number;
  present?: number;
  absent?: number;
  leave?: number;
  half_day?: number;
  skipped_students: number;
}

interface Segment {
  key: 'present' | 'absent' | 'leave' | 'skipped';
  label: string;
  value: number;
  idleGradient: string;
  hoverGradient: string;
  idleShadow: string;
  hoverShadow: string;
  textColor: string;
}

interface DayOption {
  iso: string;
  dayLabel: string;
  dateNum: string;
  isToday: boolean;
}

interface TooltipState {
  key: string;
  x: number; // bar centre, relative to plot
  y: number; // bar top, relative to plot
}

const buildLastSevenDays = (): DayOption[] => {
  const days: DayOption[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);

    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2);
    const dateNum = String(d.getDate()).padStart(2, '0');

    days.push({ iso, dayLabel, dateNum, isToday: i === 0 });
  }
  return days;
};

const formatFullDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

// ==================== LAYOUT CONSTANTS ====================
const CHART_HEIGHT = 130;
const MIN_SEGMENT = 3;
const BAR_WIDTH = 24;
const COLUMN_WIDTH = 36;
const PLOT_TOP = 14; // space above the tallest bar
const AXIS_WIDTH = 30;
const TIP_WIDTH = 170;

const UnmarkedClassesChart: React.FC<{
  classes: UnmarkedClass[];
  selectedDate?: string;
  onDateChange?: (date: string) => void;
}> = ({ classes, selectedDate, onDateChange }) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [tip, setTip] = useState<TooltipState | null>(null);
  const [animated, setAnimated] = useState(false);
  const [days] = useState<DayOption[]>(() => buildLastSevenDays());
  const [internalDate, setInternalDate] = useState<string>(
    selectedDate ?? days[days.length - 1].iso
  );
  const plotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedDate && selectedDate !== internalDate) {
      setInternalDate(selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, []);

  const handleDayClick = (iso: string) => {
    setInternalDate(iso);
    onDateChange?.(iso);
  };

  const hideTip = () => {
    setHoveredKey(null);
    setTip(null);
  };

  const showTip = (e: React.SyntheticEvent<HTMLDivElement>, key: string, barHeight: number) => {
    setHoveredKey(key);
    const plot = plotRef.current;
    const stack = e.currentTarget.querySelector('[data-bar-stack]') as HTMLElement | null;
    if (!plot || !stack) return;
    const p = plot.getBoundingClientRect();
    const b = stack.getBoundingClientRect();
    setTip({
      key,
      x: b.left - p.left + b.width / 2,
      y: b.bottom - p.top - barHeight,
    });
  };

  const activeDate = selectedDate ?? internalDate;

  if (classes.length === 0) return null;

  const unmarkedCount = classes.filter((c) => c.skipped_students > 0).length;

  const maxTotal = Math.max(...classes.map((c) => c.total_students), 1);

  const longestLabelLen = Math.max(
    ...classes.map((c) => `${c.class_name}${c.section ? '-' + c.section : ''}`.length),
    6
  );
  const LABEL_HEIGHT = Math.min(120, Math.max(60, longestLabelLen * 6.5));
  const PLOT_HEIGHT = PLOT_TOP + CHART_HEIGHT + 8 + LABEL_HEIGHT + 18;

  // y-axis ticks (0 → max, 3 steps, de-duplicated for small classes)
  const ticks = Array.from(
    new Set([0, 1, 2, 3].map((i) => Math.round((maxTotal * i) / 3)))
  );
  const tickTop = (v: number) => PLOT_TOP + CHART_HEIGHT - (v / maxTotal) * CHART_HEIGHT;

  const buildSegments = (c: UnmarkedClass): Segment[] => {
    const leaveTotal = (c.leave ?? 0) + (c.half_day ?? 0);
    const present = c.present ?? Math.max(c.attendance_count - (c.absent ?? 0) - leaveTotal, 0);
    const absent = c.absent ?? 0;

    return [
      {
        key: 'present',
        label: 'Present',
        value: present,
        idleGradient: 'linear-gradient(180deg, #bbf7d0 0%, #4ade80 100%)',
        hoverGradient: 'linear-gradient(180deg, #4ade80 0%, #15803d 100%)',
        idleShadow: '0 3px 8px rgba(34, 197, 94, 0.2)',
        hoverShadow: '0 10px 18px rgba(21, 128, 61, 0.4)',
        textColor: '#16a34a',
      },
      {
        key: 'absent',
        label: 'Absent',
        value: absent,
        idleGradient: 'linear-gradient(180deg, #fecaca 0%, #f87171 100%)',
        hoverGradient: 'linear-gradient(180deg, #f87171 0%, #b91c1c 100%)',
        idleShadow: '0 3px 8px rgba(239, 68, 68, 0.18)',
        hoverShadow: '0 10px 18px rgba(185, 28, 28, 0.4)',
        textColor: '#dc2626',
      },
      {
        key: 'leave',
        label: 'Leave/Half-day',
        value: leaveTotal,
        idleGradient: 'linear-gradient(180deg, #fde68a 0%, #f59e0b 100%)',
        hoverGradient: 'linear-gradient(180deg, #f59e0b 0%, #b45309 100%)',
        idleShadow: '0 3px 8px rgba(245, 158, 11, 0.2)',
        hoverShadow: '0 10px 18px rgba(180, 83, 9, 0.4)',
        textColor: '#b45309',
      },
      {
        key: 'skipped',
        label: 'Skipped',
        value: c.skipped_students,
        idleGradient: 'linear-gradient(180deg, #e2e8f0 0%, #94a3b8 100%)',
        hoverGradient: 'linear-gradient(180deg, #94a3b8 0%, #475569 100%)',
        idleShadow: '0 3px 8px rgba(100, 116, 139, 0.18)',
        hoverShadow: '0 10px 18px rgba(71, 85, 105, 0.4)',
        textColor: '#64748b',
      },
    ];
  };

  // ---- tooltip data (only computed for the hovered class) ----
  const tipClass = tip ? classes.find((c) => `${c.class_id}-${c.section}` === tip.key) : undefined;
  const tipSegments = tipClass ? buildSegments(tipClass) : [];
  const tipLabel = tipClass
    ? `${tipClass.class_name}${tipClass.section ? '-' + tipClass.section : ''}`
    : '';
  const tipMarkedPct = tipClass
    ? Math.round((tipClass.attendance_count / Math.max(tipClass.total_students, 1)) * 100)
    : 0;

  const plotWidth = plotRef.current?.clientWidth ?? 0;
  const half = TIP_WIDTH / 2;
  const tipLeft = tip
    ? plotWidth > TIP_WIDTH
      ? Math.min(Math.max(tip.x, half), plotWidth - half)
      : tip.x
    : 0;
  const arrowShift = tip ? tip.x - tipLeft : 0;

  return (
    <Card
      style={{
        borderRadius: '16px',
        marginBottom: '24px',
        border: '1px solid rgba(59, 130, 246, 0.15)',
        background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
      }}
    >
      <style>{`
        .uc-scroll {
          scrollbar-width: thin;
          scrollbar-color: #3b82f6 #dbeafe;
        }
        .uc-scroll::-webkit-scrollbar { height: 8px; }
        .uc-scroll::-webkit-scrollbar-track { background: #dbeafe; border-radius: 999px; }
        .uc-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #60a5fa 0%, #2563eb 100%);
          border-radius: 999px;
          border: 1px solid #dbeafe;
        }
        .uc-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(90deg, #3b82f6 0%, #1e40af 100%);
        }
        .uc-day { outline: none; }
        .uc-day:not([aria-pressed="true"]):hover { background: #e2e8f0 !important; }
        .uc-day:focus-visible { box-shadow: 0 0 0 2px #60a5fa !important; }
        .uc-col { outline: none; }
        .uc-col:focus-visible .uc-bar-stack { outline: 2px solid #60a5fa; outline-offset: 3px; border-radius: 8px; }
        @keyframes ucTipIn {
          from { opacity: 0; margin-top: 6px; }
          to   { opacity: 1; margin-top: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .uc-tip, .uc-col * { animation: none !important; transition: none !important; }
        }
      `}</style>

      {/* ── header + legend | day picker ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '20px',
          marginBottom: '20px',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: '1 1 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 4px 10px rgba(220, 38, 38, 0.25)',
              }}
            >
              <i className="pi pi-exclamation-circle" style={{ color: 'white', fontSize: '1rem' }}></i>
            </div>
            <h6 style={{ color: '#1e40af', fontWeight: 700, margin: 0, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
              {unmarkedCount} class{unmarkedCount !== 1 ? 'es' : ''} not marked yet
            </h6>
          </div>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {[
              { color: 'linear-gradient(180deg, #4ade80 0%, #16a34a 100%)', label: 'Present' },
              { color: 'linear-gradient(180deg, #f87171 0%, #dc2626 100%)', label: 'Absent' },
              { color: 'linear-gradient(180deg, #f59e0b 0%, #b45309 100%)', label: 'Leave/Half-day' },
              { color: 'linear-gradient(180deg, #94a3b8 0%, #475569 100%)', label: 'Skipped' },
            ].map((item) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.color }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* segmented day picker */}
        <div style={{ flex: '0 0 250px', minWidth: 203 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: '8px',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '0.53rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.39px' }}>
              Last 7 days
            </span>
            <span style={{ fontSize: '0.91rem', fontWeight: 700, color: '#1e40af', textAlign: 'right' }}>
              {formatFullDate(activeDate)}
            </span>
          </div>

          <div
            role="group"
            aria-label="Select date"
            style={{
              display: 'flex',
              width: '100%',
              background: '#f1f5f9',
              borderRadius: '999px',
              padding: '3px',
              border: '1px solid #e2e8f0',
              gap: '2px',
            }}
          >
            {days.map((d) => {
              const isActive = d.iso === activeDate;
              return (
                <button
                  key={d.iso}
                  type="button"
                  className="uc-day"
                  aria-pressed={isActive}
                  aria-label={formatFullDate(d.iso)}
                  onClick={() => handleDayClick(d.iso)}
                  style={{
                    flex: '1 1 0%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1px',
                    padding: '6px 2px',
                    borderRadius: '999px',
                    border: 'none',
                    background: isActive
                      ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'
                      : 'transparent',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    boxShadow: isActive ? '0 3px 8px rgba(59, 130, 246, 0.35)' : 'none',
                    position: 'relative',
                  }}
                >
                  <span
                    style={{
                      fontSize: '0.47rem',
                      fontWeight: 700,
                      color: isActive ? 'rgba(255,255,255,0.85)' : '#94a3b8',
                      textTransform: 'uppercase',
                      lineHeight: 1.1,
                    }}
                  >
                    {d.dayLabel}
                  </span>
                  <span
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      color: isActive ? '#ffffff' : '#1e293b',
                      lineHeight: 1.1,
                    }}
                  >
                    {d.dateNum}
                  </span>
                  {d.isToday && (
                    <span
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 8,
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: isActive ? '#ffffff' : '#22c55e',
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── chart: y-axis | plot (grid + scrolling bars + tooltip) ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {/* y-axis */}
        <div style={{ position: 'relative', width: AXIS_WIDTH, height: PLOT_HEIGHT, flexShrink: 0 }}>
          {ticks.map((v) => (
            <span
              key={v}
              style={{
                position: 'absolute',
                right: 6,
                top: tickTop(v) - 7,
                fontSize: '0.65rem',
                fontWeight: 600,
                color: '#94a3b8',
                lineHeight: '14px',
              }}
            >
              {v}
            </span>
          ))}
        </div>

        {/* plot */}
        <div ref={plotRef} style={{ position: 'relative', flex: '1 1 0%', minWidth: 0, height: PLOT_HEIGHT }}>
          {/* grid lines (do not scroll) */}
          {ticks.map((v) => (
            <div
              key={v}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: tickTop(v),
                borderTop: v === 0 ? '1px solid #cbd5e1' : '1px dashed #e2e8f0',
                pointerEvents: 'none',
              }}
            />
          ))}

          {/* Scrolling bars row.
              `safe center` keeps bars centered while they fit, and falls back to
              start-alignment on overflow so the first classes stay reachable. */}
          <div
            className="uc-scroll"
            onScroll={hideTip}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-end',
              gap: '4px',
              overflowX: 'auto',
              overflowY: 'hidden',
              height: '100%',
              paddingTop: PLOT_TOP,
              paddingLeft: '8px',
              paddingRight: '8px',
              paddingBottom: '18px',
              justifyContent: 'safe center',
            }}
          >
            {classes.map((c, index) => {
              const key = `${c.class_id}-${c.section}`;
              const isHovered = hoveredKey === key;
              const isDimmed = hoveredKey !== null && !isHovered;
              const label = `${c.class_name}${c.section ? '-' + c.section : ''}`;
              const total = Math.max(c.total_students, 1);
              const barCapacity = (total / maxTotal) * CHART_HEIGHT;

              const segments = buildSegments(c);
              const finalHeights = segments.map((s) =>
                Math.max(s.value > 0 ? MIN_SEGMENT : 0, (s.value / total) * barCapacity)
              );
              const heights = finalHeights.map((h) => (animated ? h : 0));
              const totalFinalHeight = finalHeights.reduce((a, b) => a + b, 0);

              return (
                <div
                  key={key}
                  className="uc-col"
                  tabIndex={0}
                  aria-label={`${label}: ${segments.map((s) => `${s.label} ${s.value}`).join(', ')}, total ${c.total_students}`}
                  style={{
                    position: 'relative',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: COLUMN_WIDTH,
                    cursor: 'pointer',
                    opacity: isDimmed ? 0.45 : 1,
                    transition: 'opacity 0.2s ease',
                  }}
                  onMouseEnter={(e) => showTip(e, key, totalFinalHeight)}
                  onMouseLeave={hideTip}
                  onFocus={(e) => showTip(e, key, totalFinalHeight)}
                  onBlur={hideTip}
                >
                  {/* bar stack */}
                  <div
                    data-bar-stack
                    className="uc-bar-stack"
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      height: CHART_HEIGHT,
                      width: BAR_WIDTH,
                      transform: isHovered ? 'scaleX(1.12)' : 'scaleX(1)',
                      transformOrigin: 'bottom center',
                      transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    }}
                  >
                    {(() => {
                      const renderOrder = [...segments].reverse();
                      const renderHeights = renderOrder.map((_, sIndex) => heights[segments.length - 1 - sIndex]);
                      const renderFinalHeights = renderOrder.map((_, sIndex) => finalHeights[segments.length - 1 - sIndex]);
                      const topVisibleIdx = renderFinalHeights.findIndex((h) => h > 0);
                      let bottomVisibleIdx = -1;
                      for (let i = renderFinalHeights.length - 1; i >= 0; i--) {
                        if (renderFinalHeights[i] > 0) {
                          bottomVisibleIdx = i;
                          break;
                        }
                      }

                      return renderOrder.map((s, sIndex) => {
                        const h = renderHeights[sIndex];
                        const isTopVisible = sIndex === topVisibleIdx;
                        const isBottomVisible = sIndex === bottomVisibleIdx;
                        const showDivider = h > 0 && !isBottomVisible;
                        return (
                          <div
                            key={s.key}
                            style={{
                              width: '100%',
                              height: `${h}px`,
                              background: isHovered ? s.hoverGradient : s.idleGradient,
                              borderRadius: h > 0
                                ? `${isTopVisible ? '8px 8px' : '0 0'} ${isBottomVisible ? '6px 6px' : '0 0'}`
                                : 0,
                              borderBottom: showDivider ? '2px solid #ffffff' : 'none',
                              boxShadow: h > 0 ? (isHovered ? s.hoverShadow : s.idleShadow) : 'none',
                              transition: `height 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.03 + sIndex * 0.03}s, background 0.25s ease, box-shadow 0.25s ease`,
                            }}
                          />
                        );
                      });
                    })()}
                  </div>

                  {/* class label */}
                  <div
                    style={{
                      marginTop: '8px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      color: isHovered ? '#dc2626' : '#64748b',
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      height: `${LABEL_HEIGHT}px`,
                      width: '14px',
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* tooltip — lives outside the scroller so it never gets clipped */}
          {tip && tipClass && (
            <>
              {/* marker dot on top of the hovered bar */}
              <div
                style={{
                  position: 'absolute',
                  left: tip.x,
                  top: tip.y - 9,
                  width: 7,
                  height: 7,
                  marginLeft: -3.5,
                  borderRadius: '50%',
                  background: '#dc2626',
                  border: '1.5px solid #ffffff',
                  pointerEvents: 'none',
                  zIndex: 19,
                }}
              />
              <div
                className="uc-tip"
                style={{
                  position: 'absolute',
                  left: tipLeft,
                  top: tip.y - 16,
                  transform: 'translate(-50%, -100%)',
                  width: TIP_WIDTH,
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '10px 12px',
                  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.18)',
                  border: '1px solid #e2e8f0',
                  zIndex: 20,
                  pointerEvents: 'none',
                  animation: 'ucTipIn 0.15s ease-out',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b' }}>{tipLabel}</span>
                  <span
                    style={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: '#1e40af',
                      background: '#eff6ff',
                      border: '1px solid rgba(59, 130, 246, 0.25)',
                      borderRadius: '999px',
                      padding: '2px 8px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tipMarkedPct}% marked
                  </span>
                </div>

                {tipSegments.map((s) =>
                  s.value > 0 || s.key !== 'leave' ? (
                    <div
                      key={s.key}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '2px 0' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', fontWeight: 600, color: s.textColor }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.idleGradient }} />
                        {s.label}
                      </span>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: s.textColor }}>{s.value}</span>
                    </div>
                  ) : null
                )}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '6px',
                    paddingTop: '6px',
                    borderTop: '1px solid #e2e8f0',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: '#1e293b',
                  }}
                >
                  <span>Total</span>
                  <span>{tipClass.total_students}</span>
                </div>

                {/* pointer arrow */}
                <div
                  style={{
                    position: 'absolute',
                    left: `calc(50% + ${arrowShift}px)`,
                    bottom: -6,
                    width: 10,
                    height: 10,
                    background: '#ffffff',
                    borderRight: '1px solid #e2e8f0',
                    borderBottom: '1px solid #e2e8f0',
                    transform: 'translateX(-50%) rotate(45deg)',
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default UnmarkedClassesChart;